import * as React from 'react';
import { useQueryClient } from 'react-query';
import { useApiUrl } from '../../../../../../../shared/hooks/useApiUrl';
import { preloadImage } from '../../../cache';
import ImageProcessingApi from '../../../api/ImageProcessingApi';
import { IImageThumbModalViewModel, IImageThumbnail } from '../../../Entities';

type PreviewImage = {
    id: number;
    src: string;
    filename: string;
    categoryId: number;
};

type ResolvedPreview = {
    src: string;
    filename: string;
    categoryId: number;
};

type UseImagePreviewLoadingParams = {
    orderId: number;
    images: IImageThumbnail[];
    photoStatus: number;
};

let CACHE_STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes

function editedImageQueryKey(orderId: number, imageId: number) {
    return ['editedImage', orderId, imageId] as const;
}

export function useImagePreviewLoading({
    orderId,
    images,
    photoStatus
}: UseImagePreviewLoadingParams) {
    let apiUrl = useApiUrl();
    let queryClient = useQueryClient();

    let [previewImage, setPreviewImage] = React.useState<PreviewImage | null>(null);
    let previousPhotoStatusRef = React.useRef(photoStatus);

    let resolvePreviewFn = React.useCallback(
        function resolvePreview(
            result: IImageThumbModalViewModel,
            image: IImageThumbnail | undefined
        ): ResolvedPreview {
            let editedImages = result.imageShowcaseViewModels;
            let latestImage = editedImages[editedImages.length - 1];
            return {
                src: apiUrl + '/file/' + latestImage.reference,
                filename: latestImage.filename,
                categoryId: image ? image.categoryId : 0
            };
        },
        [apiUrl]
    );

    let fetchEditedImageFn = React.useCallback(
        function fetchEditedImage(imageId: number) {
            return ImageProcessingApi.getEditedImage(orderId, imageId);
        },
        [orderId]
    );

    // Invalidate all cached previews when photoStatus changes
    React.useEffect(() => {
        if (previousPhotoStatusRef.current !== photoStatus) {
            queryClient.invalidateQueries(['editedImage', orderId]);
            previousPhotoStatusRef.current = photoStatus;
        }
    }, [photoStatus, orderId, queryClient]);

    let prefetchPreview = React.useCallback(
        function (imageId: number) {
            let image = images.find((image) => {
                return image.id === imageId;
            });

            queryClient
                .prefetchQuery(
                    editedImageQueryKey(orderId, imageId),
                    () => fetchEditedImageFn(imageId),
                    { staleTime: CACHE_STALE_TIME_MS }
                )
                .then(() => {
                    let cached = queryClient.getQueryData<IImageThumbModalViewModel>(
                        editedImageQueryKey(orderId, imageId)
                    );
                    if (cached) {
                        let resolved = resolvePreviewFn(cached, image);
                        preloadImage(resolved.src);
                    }
                });
        },
        [images, orderId, queryClient, fetchEditedImageFn, resolvePreviewFn]
    );

    function openPreview(imageId: number) {
        let image = images.find((image) => {
            return image.id === imageId;
        });

        let cached = queryClient.getQueryData<IImageThumbModalViewModel>(
            editedImageQueryKey(orderId, imageId)
        );

        if (cached) {
            let resolved = resolvePreviewFn(cached, image);
            setPreviewImage({
                id: imageId,
                src: resolved.src,
                filename: resolved.filename,
                categoryId: resolved.categoryId
            });
            return;
        }

        setPreviewImage({
            id: imageId,
            src: '',
            filename: image ? image.filename : '',
            categoryId: image ? image.categoryId : 0
        });

        queryClient
            .fetchQuery(editedImageQueryKey(orderId, imageId), () => fetchEditedImageFn(imageId), {
                staleTime: CACHE_STALE_TIME_MS
            })
            .then((result) => {
                let resolved = resolvePreviewFn(result, image);
                setPreviewImage({
                    id: imageId,
                    src: resolved.src,
                    filename: resolved.filename,
                    categoryId: resolved.categoryId
                });
            });
    }

    function updateCachedFilename(imageId: number, newFilename: string) {
        queryClient.setQueryData<IImageThumbModalViewModel | undefined>(
            editedImageQueryKey(orderId, imageId),
            function handleUpdate(oldImage) {
                if (!oldImage) {
                    return oldImage;
                }

                let editedImages = oldImage.imageShowcaseViewModels;
                let updated = [...editedImages];

                updated[updated.length - 1] = {
                    ...updated[updated.length - 1],
                    filename: newFilename
                };

                return { ...oldImage, imageShowcaseViewModels: updated };
            }
        );
    }

    function invalidatePreview(imageId: number) {
        queryClient.invalidateQueries(editedImageQueryKey(orderId, imageId));
    }

    function closePreview() {
        setPreviewImage(null);
    }

    return {
        previewImage,
        setPreviewImage,
        updateCachedFilename,
        invalidatePreview,
        prefetchPreview,
        openPreview,
        closePreview
    };
}
